import React, { useRef, useState, useEffect } from 'react';
import SelectSymbol from '../../../components/SelectSymbol/SelectSymbol';
import SelectSide from '../../../components/NewOrder/SelectSide';
import OrderType from '../../../components/NewOrder/OrderType';
import QuantityTemplate from './QuantityTemplate';
import PriceTemplate from './PriceTemplate';
import { processError } from '../../../services/ServiceUtils';
import ActionMessage from '../../../components/ActionMessage/ActionMessage';
import { saveOrderTemplate } from '../../../services/OrderTemplatesService';
import { STOP_TYPES, orderTypes, calcTrailingStop } from '../../../utils/OrderUtils';
import { getIndexes } from '../../../services/BeholderService'
import TrailingTemplate from './TrailingTemplate';
import PartialTpTemplate from './PartialTpTemplate';
import RegimeSizingTemplate from './RegimeSizingTemplate';
import RegimeStopTemplate from './RegimeStopTemplate';
import { getValueOrDefault, initializeModal } from '../../../utils/ModalUtils';
import { DEFAULT_ORDER_TEMPLATE } from '../../../utils/OrderTemplateUtils';
import { getCurrentMarket, MARKET_FUTURES } from '../../../utils/MarketStorage';
// import { indexKeys } from '../../../utils/IndexUtils';

// Template novo já nasce "futuros" quando o mercado atual é Futuros (UX da Fase 3.4).
function freshTemplate() {
    return { ...DEFAULT_ORDER_TEMPLATE, isFuture: getCurrentMarket() === MARKET_FUTURES };
}

// Detecta se o modelo já usa recursos avançados (escondidos no modo Simples) — p/ avisar sem poluir.
function usesAdvancedFeatures(t) {
    return !!(t.tp1Percent > 0 || t.tp2Percent > 0 || t.tp3Trailing
        || t.regimeSizing || t.regimeStopMultiplier || t.useRankMultiplier);
}

/**
 * props:
 * - onSubmit
 */
function OrderTemplateModal(props) {

    const [error, setError] = useState('');
    const [indexes, setIndexes] = useState([]);

    const [orderTemplate, setOrderTemplate] = useState(DEFAULT_ORDER_TEMPLATE);
    // Modo do formulário: 'simple' (essenciais) ou 'advanced' (todos os add-ons). Só apresentação → localStorage.
    const [formMode, setFormMode] = useState(() => localStorage.getItem('orderTemplateFormMode') || 'simple');
    const changeFormMode = m => { setFormMode(m); try { localStorage.setItem('orderTemplateFormMode', m); } catch (_) { } };

    const btnClose = useRef('');
    const btnSave = useRef('');

    function onSubmit(event) {
        saveOrderTemplate(orderTemplate.id, orderTemplate)
            .then(result => {
                btnClose.current.click();
                if (props.onSubmit) props.onSubmit(result);
            })
            .catch(err => {
                setError(processError(err));
                // if (err.response && err.response.status === 401) {
                //     btnClose.current.click();
                // }
            })
    }

    function calcTrailingStopOrderTemplate(orderTemplate) {
        if (!orderTemplate.stopPriceMultiplier || !orderTemplate.limitPrice) return;

        return calcTrailingStop(orderTemplate.side, orderTemplate.limitPrice, orderTemplate.stopPriceMultiplier);
    }

    function checkIsTrailingStopOrderTemplate() {
        if (orderTemplate.type !== orderTypes.TRAILING_STOP) return;

        let stopPrice = calcTrailingStopOrderTemplate(orderTemplate);
        if (!stopPrice) return;

        stopPrice = stopPrice.toString();
        console.log(stopPrice);
        setOrderTemplate(prevState => ({ ...prevState, stopPrice }));
    }

    function onInputChange(event) {
        setOrderTemplate(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
        checkIsTrailingStopOrderTemplate();
    }

    useEffect(() => {
        if (!orderTemplate || !orderTemplate.symbol) return;

        getIndexes()
            .then(indexes => {
                // indexKeys.BOOK | indexKeys.LAST_CANDLE | indexKeys.LAST_ORDER
                const indexesRegex = /^(BOOK|LAST_CANDLE|LAST_ORDER.(avgPrice|stopPrice|limitPrice))/;
                const filteredIndexes = indexes.filter(k => k.symbol === orderTemplate.symbol && indexesRegex.test(k.variable));
                setIndexes(filteredIndexes);
            })
            .catch(err => setError(processError(err)))
    }, [orderTemplate.symbol]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setError('');

        if (!props.data || !props.data.id)
            setOrderTemplate(freshTemplate());
        else
            setOrderTemplate(props.data);

    }, [props.data]);

    function getPriceClasses(orderType) {
        if (!orderType) return "col-md-6 mb-3 d-none";

        return [orderTypes.MARKET, orderTypes.STOP_LOSS, orderTypes.TAKE_PROFIT, orderTypes.TRAILING_STOP].includes(orderType) ? "col-md-6 mb-3 d-none" : "col-md-6 mb-3";
    }

    function getIcebergClasses(orderType) {
        return orderType === orderTypes.ICEBERG ? "col-md-6 mb-3" : "col-md-6 mb-3 d-none";
    }

    function getStopPriceClasses(orderType) {
        return STOP_TYPES.indexOf(orderType) !== -1 ? "col-md-6 mb-3" : "col-md-6 mb-3 d-none";
    }

    useEffect(() => {
        initializeModal('modalOrderTemplate', () => setOrderTemplate(freshTemplate()));
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    return (
        <div className="modal fade" id="modalOrderTemplate" tabIndex="-1" role="dialog" aria-labelledby="modalTitleNotify" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">{orderTemplate.id ? 'Editar' : 'Novo'} Modelo de Ordem</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>

                    <div className="modal-body">
                        <div className='form-group'>

                            {/* Toggle Simples ↔ Avançado — só apresentação (localStorage). Simples esconde os add-ons. */}
                            <div className='d-flex justify-content-end mb-2'>
                                <div className='btn-group btn-group-sm' role='group' aria-label='Modo do formulário'>
                                    <button type='button' className={`btn ${formMode === 'simple' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('simple')}>Simples</button>
                                    <button type='button' className={`btn ${formMode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => changeFormMode('advanced')}>Avançado</button>
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-4 mb-3'>
                                    <div className="form-group">
                                        <label htmlFor="symbol">Símbolo</label>
                                        <SelectSymbol symbol={orderTemplate.symbol} onChange={onInputChange} disabled={orderTemplate.id > 0} />
                                    </div>
                                </div>
                                <div className='col-md-4 mb-3'>
                                    <SelectSide side={orderTemplate.side} onChange={onInputChange} />
                                </div>
                                <div className='col-md-4 mb-3'>
                                    <OrderType type={orderTemplate.type} onChange={onInputChange} />
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-12 mb-3'>
                                    <div className='form-group'>
                                        <label htmlFor='name'>Nome:</label>
                                        <input type='text' className='form-control' id='name' placeholder="Nome do meu modelo" value={getValueOrDefault(orderTemplate.name)} onChange={onInputChange} />
                                    </div>
                                </div>
                            </div>
                            {/* FUTUROS (Fase 3.4): marca o template p/ a ação FUTURES_ORDER */}
                            <div className='row'>
                                <div className='col-12 mb-2'>
                                    <div className='form-check'>
                                        <input className='form-check-input' type='checkbox' id='isFuture'
                                            checked={!!orderTemplate.isFuture}
                                            onChange={e => onInputChange({ target: { id: 'isFuture', value: e.target.checked } })} />
                                        <label className='form-check-label' htmlFor='isFuture'><strong>Template de Futuros</strong> (alavancado)</label>
                                    </div>
                                </div>
                            </div>
                            {orderTemplate.isFuture && (
                                <div className='row border rounded mx-0 mb-3 py-2' style={{ background: 'rgba(245,158,11,0.06)' }}>
                                    <div className='col-md-3 mb-2'>
                                        <label htmlFor='leverage'>Alavancagem</label>
                                        <input type='number' className='form-control' id='leverage' placeholder='ex: 3'
                                            value={getValueOrDefault(orderTemplate.leverage)} onChange={onInputChange} />
                                    </div>
                                    <div className='col-md-3 mb-2'>
                                        <label htmlFor='marginType'>Margem</label>
                                        <select id='marginType' className='form-select' value={getValueOrDefault(orderTemplate.marginType)} onChange={onInputChange}>
                                            <option value=''>(manter)</option>
                                            <option value='ISOLATED'>ISOLATED</option>
                                            <option value='CROSSED'>CROSSED</option>
                                        </select>
                                    </div>
                                    <div className='col-md-6 mb-2 d-flex align-items-end'>
                                        <div className='form-check'>
                                            <input className='form-check-input' type='checkbox' id='reduceOnly'
                                                checked={!!orderTemplate.reduceOnly}
                                                onChange={e => onInputChange({ target: { id: 'reduceOnly', value: e.target.checked } })} />
                                            <label className='form-check-label' htmlFor='reduceOnly'>Reduce Only (saída — só reduz/fecha)</label>
                                        </div>
                                    </div>
                                    <div className='col-12'>
                                        <small className='text-muted'>
                                            No campo <strong>Quantity</strong> use: <code>10%</code> = 10% da margem × alavancagem (entrada),
                                            <code> POSITION</code> = fecha a posição inteira (saída), ou um número de contratos.
                                            <strong> Stop Price</strong> (multiplicador) e <strong>TP1 %</strong> definem a proteção do short.
                                        </small>
                                    </div>
                                </div>
                            )}
                            {
                                orderTemplate.type === orderTypes.TRAILING_STOP
                                    ? <TrailingTemplate data={orderTemplate} onChange={onInputChange} />
                                    : <React.Fragment></React.Fragment>
                            }
                            <div className='row'>
                                <div className={getPriceClasses(orderTemplate.type)}>
                                    <PriceTemplate id='limitPrice' text='Unit Price:' indexes={indexes} onChange={onInputChange} price={orderTemplate.limitPrice} multiplier={orderTemplate.limitPriceMultiplier} />
                                </div>
                                <div className={getStopPriceClasses(orderTemplate.type)}>
                                    <PriceTemplate id='stopPrice' text='Stop Price:' indexes={indexes} onChange={onInputChange} price={orderTemplate.stopPrice} multiplier={orderTemplate.stopPriceMultiplier} />
                                </div>
                            </div>

                            <div className='row'>
                                <div className='col-md-6 mb-3'>
                                    <QuantityTemplate id='quantity' text='Quantity:' quantity={orderTemplate.quantity} multiplier={orderTemplate.quantityMultiplier} onChange={onInputChange} />
                                </div>
                                <div className={getIcebergClasses(orderTemplate.type)}>
                                    <QuantityTemplate id='icebergQty' text='Iceberg Qty:' quantity={orderTemplate.icebergQty} multiplier={orderTemplate.icebergQtyMultiplier} onChange={onInputChange} />
                                </div>
                            </div>

                            {formMode === 'advanced' && (
                                <React.Fragment>
                                    {orderTemplate.side === 'BUY' && (
                                        <PartialTpTemplate data={orderTemplate} onChange={onInputChange} />
                                    )}

                                    <RegimeSizingTemplate regimeSizing={orderTemplate.regimeSizing} onChange={onInputChange} />

                                    {STOP_TYPES.includes(orderTemplate.type) && (
                                        <RegimeStopTemplate regimeStopMultiplier={orderTemplate.regimeStopMultiplier} onChange={onInputChange} />
                                    )}

                                    <div className='row mb-3'>
                                        <div className='col-12'>
                                            <div className='form-check form-switch'>
                                                <input
                                                    className='form-check-input'
                                                    type='checkbox'
                                                    id='useRankMultiplier'
                                                    checked={!!orderTemplate.useRankMultiplier}
                                                    onChange={e => onInputChange({ target: { id: 'useRankMultiplier', value: e.target.checked } })}
                                                />
                                                <label className='form-check-label' htmlFor='useRankMultiplier'>
                                                    Position sizing por rank do scanner
                                                </label>
                                                <small className='d-block text-muted mt-1'>
                                                    Quando ativo, a quantidade é escalada pela posição do símbolo no top do scanner IA:
                                                    top-1 leva 1.5×, top-3 leva 1.0×, top-5+ leva 0.55×.
                                                    Concentra capital nas melhores oportunidades do momento.
                                                    Sem efeito se o símbolo não estiver no foco do scanner.
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            )}

                            {/* Transparência: no Simples, avisa (compacto) se o modelo já usa recursos escondidos */}
                            {formMode === 'simple' && usesAdvancedFeatures(orderTemplate) && (
                                <div className='alert alert-light border py-2 small mb-2'>
                                    ⚙️ Este modelo usa recursos avançados (alvos parciais / sizing por regime / por rank). Eles continuam ativos — abra <strong>Avançado</strong> pra ver ou editar.
                                </div>
                            )}

                        </div>
                    </div>

                    <div className="modal-footer">
                        <ActionMessage error={error} extraClassName="mt-1 col-9 py-1" />
                        <button ref={btnSave} type="button" className="btn btn-sm btn-primary" onClick={onSubmit}>
                            <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" /></svg>
                            Salvar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OrderTemplateModal;